import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../../shadcn/ui/button";
import { Badge } from "../../shadcn/ui/badge";
import { ArrowRight, Music2, Wine, UtensilsCrossed, Mountain, Star, ShieldCheck, Crown } from "lucide-react";

const highlightSpaces = [
  {
    title: "The Base: Offices Adjozan",
    price: "₦85/day",
    type: "Work",
    distance: "2 mins drive",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1200&auto=format&fit=crop",
  },
  {
    title: "Regus / Easy Offices",
    price: "₦170/day",
    type: "Work",
    distance: "5 mins drive",
    image: "https://images.unsplash.com/photo-1433832597046-4f10e10ac764?q=80&w=1200&auto=format&fit=crop",
  },
  {
    title: "Work & Co Elibro ISL",
    price: "₦85/day",
    type: "Work",
    distance: "4 mins drive",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1200&auto=format&fit=crop",
  },
  {
    title: "Spaces V&A Waterfront",
    price: "₦200/day",
    type: "Work",
    distance: "5 mins drive",
    image: "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?q=80&w=1200&auto=format&fit=crop",
  },
];

const experienceSections = [
  {
    id: "entertainment",
    icon: Music2,
    title: "Cape Town Entertainment",
    items: [
      { title: "V&A Waterfront Live Music", cta: "Explore" },
      { title: "Zeitz MOCAA", cta: "Explore" },
    ],
  },
  {
    id: "wine",
    icon: Wine,
    title: "Winelands Tastings",
    items: [
      { title: "Constantia Wine Route", cta: "Explore" },
      { title: "Wine & Jazz Pairing", cta: "Explore" },
    ],
  },
  {
    id: "dining",
    icon: UtensilsCrossed,
    title: "Group Dining",
    items: [
      { title: "FYN Restaurant", cta: "Explore" },
      { title: "Gold Restaurant", cta: "Explore" },
    ],
  },
  {
    id: "sunset",
    icon: Mountain,
    title: "Sunset & Scenic",
    items: [
      { title: "Table Mountain Cableway", cta: "Explore" },
      { title: "Signal Hill Sundowners", cta: "Explore" },
    ],
  },
];

const benefits = [
  { icon: Star, title: "Premium Access", desc: "Preferred rates on workspaces and events." },
  { icon: ShieldCheck, title: "Exclusive Discounts", desc: "Special offers curated for delegates." },
  { icon: Crown, title: "VIP Experiences", desc: "Priority reservations across top venues." },
];

const AfricaTechFestivalPage = () => {
  return (
    <div className="bg-white text-gray-900">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 via-teal-500 to-indigo-600" />
        <div className="relative max-w-6xl mx-auto px-6 py-16 lg:py-20 flex flex-col lg:flex-row items-center gap-10 text-white">
          <div className="flex-1 space-y-6">
            <Badge className="bg-black/40 text-white border border-white/20 w-fit">
              Cape Town International Convention Centre (CTICC)
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold leading-tight">
              AFRICA TECH FESTIVAL <span className="text-lime-300">2025</span>
            </h1>
            <p className="text-lg max-w-xl">
              SPACIA gives you the keys to the city’s best spaces, whether you’re booking a workbase, a place to stay,
              or your next adventure.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100">
                View All Experiences <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Start Booking
              </Button>
            </div>
          </div>
          <div className="flex-1 w-full">
            <div className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur">
              <img
                src="https://images.unsplash.com/photo-1550439062-609e1531270e?q=80&w=1200&auto=format&fit=crop"
                alt="Festival"
                className="w-full h-72 object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Work Near CTICC */}
      <section className="max-w-6xl mx-auto px-6 py-16 space-y-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold">Work Near CTICC</h2>
            <p className="text-gray-600 mt-2">Flexible workspaces with premium amenities.</p>
          </div>
          <Button variant="outline">View All</Button>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {highlightSpaces.map(space => (
            <div
              key={space.title}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition"
            >
              <div className="h-36 w-full bg-gray-100">
                <img src={space.image} alt={space.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-4 space-y-3">
                <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50">
                  {space.type}
                </Badge>
                <h3 className="font-semibold text-gray-900">{space.title}</h3>
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>{space.price}</span>
                  <span className="text-emerald-600">{space.distance}</span>
                </div>
                <Button variant="outline" size="sm" className="w-full">
                  Book
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Experiences */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-6xl mx-auto px-6 space-y-10">
          <div className="text-center">
            <h2 className="text-2xl md:text-3xl font-bold">Unforgettable Experiences in Cape Town</h2>
            <p className="text-gray-600 mt-2">
              Curated activities and adventures to make your conference trip memorable.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {experienceSections.map(section => (
              <div key={section.id} className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 space-y-4">
                <div className="flex items-center gap-3">
                  <section.icon className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-lg font-semibold">{section.title}</h3>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {section.items.map(item => (
                    <div
                      key={item.title}
                      className="bg-gray-50 rounded-lg p-4 border border-gray-100 flex items-center justify-between"
                    >
                      <span className="font-medium text-gray-800">{item.title}</span>
                      <Button variant="ghost" size="sm" className="text-emerald-700 hover:text-emerald-800">
                        {item.cta}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Button variant="outline">View All Experiences</Button>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-gradient-to-r from-indigo-800 via-purple-800 to-indigo-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-6 space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold">Exclusive Benefits for Africa Tech Festival Delegates</h2>
            <p className="text-white/80">
              Enjoy premium workspaces, curated experiences, and special discounts — only with SPACIA.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-3">
            {benefits.map(benefit => (
              <div key={benefit.title} className="bg-white/5 border border-white/10 rounded-xl p-5 space-y-3">
                <benefit.icon className="w-6 h-6 text-lime-300" />
                <h3 className="font-semibold text-white">{benefit.title}</h3>
                <p className="text-white/80 text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
          <div className="flex justify-center">
            <Button size="lg" className="bg-white text-indigo-900 hover:bg-gray-100">
              Start Booking
            </Button>
          </div>
        </div>
      </section>

      {/* Membership */}
      <section className="max-w-6xl mx-auto px-6 py-16 grid gap-10 lg:grid-cols-2 items-center">
        <div className="space-y-4">
          <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50 w-fit">
            One Membership
          </Badge>
          <h3 className="text-3xl font-bold">Multiple Locations</h3>
          <p className="text-gray-600">
            Join as an individual or team and access premium spaces across the city with flexible terms and exclusive
            partner perks.
          </p>
          <div className="flex items-center gap-3">
            <Button className="bg-emerald-600 hover:bg-emerald-700">Join as an Individual</Button>
            <Button variant="outline">Join as a Team</Button>
          </div>
        </div>
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-100 via-white to-indigo-100 rounded-3xl blur-2xl" />
          <div className="relative bg-white border border-gray-200 rounded-3xl p-8 shadow-lg flex items-center justify-between gap-6">
            <div className="space-y-4">
              <h4 className="text-xl font-semibold text-gray-900">Discover SPACIA World</h4>
              <p className="text-gray-600 text-sm">One pass for workspaces, experiences, and member perks.</p>
              <div className="flex gap-3">
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                  alt="Google Play"
                  className="h-10"
                />
                <img
                  src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                  alt="App Store"
                  className="h-10"
                />
              </div>
            </div>
            <div className="w-40 h-72 bg-gray-100 rounded-2xl overflow-hidden shadow-inner">
              <img
                src="https://images.unsplash.com/photo-1524504388940-b1c1722653e1?q=80&w=800&auto=format&fit=crop"
                alt="App preview"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AfricaTechFestivalPage;
